import React, { useState } from "react";

import { useModals } from "src/components/Modals";
import { Button, Color, Flex, Icon, Text, SubgridTable } from "src/elements";
import { Category } from "@CommonApi/categories";
import { useMenu } from "src/components/Menu";
import { Tooltip, TruncatedStatusCell } from "src/components";
import styled from "styled-components";
import { useApi } from "src/api";
import { CdtOrderButton } from "./CdtOrderButton";
import { CdtTreeButton } from "./CdtTreeButton";
import { CdtStudioConfigurationButton } from "./CdtStudioConfigurationButton";
import { CdtVersionSummary } from "./CdtVersionSummary";
import { DistributionButton } from "./DistributionButton";
import {
  ResponseGetCategories_categoryId_BatchGenerateItems,
  UploadStatus,
} from "@CommonApi/categories/_categoryId_/batch-generate-items";
import { useAsyncState } from "src/hooks/useAsyncState";
import { useTranslation } from "src/i18n";

const SubCategoryContainer = styled(Flex)`
  &:last-of-type {
    margin-bottom: 10px;
  }
`;

interface Props {
  category: Category;
  loading?: boolean;
  onEdit: (category: Category) => void;
  onImportStoreSummary: (category: Category) => void;
  onImportVariantRestriction: (category: Category) => void;
  onDescribeVariantRestriction: (category: Category) => void;
  onImportProductImages: () => void;
  onImportJdaBaseline: (category: Category) => void;
  onImportPdfSummaryExclusions: (category: Category) => void;
  isJdaImportEnabled: boolean;
  onStoreFixture: (category: Category) => void;
  onStatusChange: () => void;
}

const uploadStatusToString = (uploadStatus: UploadStatus): string => {
  if (!uploadStatus.error) {
    return uploadStatus.status;
  } else {
    return uploadStatus.status + "\n" + uploadStatus.error;
  }
};

export const SubCategory = ({
  category,
  onImportStoreSummary,
  onImportVariantRestriction,
  onDescribeVariantRestriction,
  onImportProductImages,
  onImportJdaBaseline,
  onImportPdfSummaryExclusions,
  isJdaImportEnabled,
  onStoreFixture,
  onEdit,
  onStatusChange,
}: Props) => {
  const { putCategories_categoryId_StatusApi, getCategories_categoryId_BatchGenerateItems } =
    useApi();
  const { t } = useTranslation("planogramDelivery");
  const { openConfirmModal } = useModals();
  const { openMenu } = useMenu();

  const [batchGenerateItems, setBatchGenerateItems] =
    useAsyncState<ResponseGetCategories_categoryId_BatchGenerateItems>();
  const [isLoadingBatchGenerateItems, setIsLoadingBatchGenerateItems] = useState(false);
  // The CDT counts load on hover, not with the page.
  const [isCdtSummaryArmed, setIsCdtSummaryArmed] = useState(false);

  const isArchived = category.status === "ARCHIVED";
  const isActive = category.status === "ACTIVE";

  const loadBatchGenerateItems = async (categoryId: number) => {
    setIsLoadingBatchGenerateItems(true);
    const response = await getCategories_categoryId_BatchGenerateItems({
      categoryId: categoryId.toString(),
    });
    if (response) {
      setBatchGenerateItems(response);
    }
    setIsLoadingBatchGenerateItems(false);
  };

  const openArchiveCategoryConfirmModal = () => {
    openConfirmModal({
      title: `Are you sure you want to archive category ${category.code}?`,
      icon: { name: "alert", color: Color.yellow },
      buttons: [
        {
          id: "back",
          label: "Back",
          color: "primary",
          variant: "inverted",
        },
        {
          id: "archive",
          label: "Archive",
          color: "yellow",
          onClick: archiveCategory,
        },
      ],
    });
  };

  const archiveCategory = async () => {
    const response = await putCategories_categoryId_StatusApi(category.id, { status: "ARCHIVED" });
    if (response) onStatusChange();
  };

  const unarchiveCategory = async () => {
    const response = await putCategories_categoryId_StatusApi(category.id, { status: "ACTIVE" });
    if (response) onStatusChange();
  };

  const openMoreMenu = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    openMenu({
      event,
      menu: {
        options: [
          {
            label: "Edit",
            onClick: () => onEdit(category),
            icon: { name: "edit", color: Color.primary },
          },
          {
            label: isActive ? "Archive" : "Unarchive",
            icon: { name: "delete", color: Color.yellow },
            onClick: isActive ? openArchiveCategoryConfirmModal : unarchiveCategory,
          },
        ],
      },
    });
  };

  return (
    <SubCategoryContainer column gap="10px">
      <Flex padding="10px" margin="0 10px" borderColor={Color.lightGrayHover} borderRadius="3px">
        <Flex column justify="space-between" gap="10px" flexGrow={1}>
          <Flex justify="between" gap="10px" align="center">
            <Text variant="caption1" color={Color.textSecondary}>
              {category.code}
            </Text>

            <Flex gap="18px" align="center">
              {!category?.isBound && (
                <Flex>
                  <Icon name="alert" color={Color.yellow} size={14} />

                  <Tooltip>
                    No stores are bound to this category.
                    <br />
                    To generate Planograms, bind a store in the "stores" page,
                    <br />
                    available in the navigation panel on the left
                  </Tooltip>
                </Flex>
              )}

              {isArchived && (
                <Text variant="caption2" color={Color.red}>
                  Category Archived
                </Text>
              )}

              <Button
                name="more-menu"
                variant="borderless"
                iconRight={{ name: "more" }}
                onClick={openMoreMenu}
              />
            </Flex>
          </Flex>

          <Flex gap="16px">
            <Flex
              column
              gap="5px"
              onMouseEnter={() => setIsCdtSummaryArmed(true)}
              onFocus={() => setIsCdtSummaryArmed(true)}
            >
              <Text variant="small4" color={Color.spaceGrayHover}>
                Merchandising Guidelines
              </Text>

              <Flex gap="10px">
                <CdtOrderButton
                  categoryId={category.id}
                  categoryCode={category.code}
                  subCategory={category.categoryGroup}
                />
                <CdtTreeButton categoryId={category.id} isDisabled={isArchived} />
                <CdtStudioConfigurationButton categoryId={category.id} isDisabled={isArchived} />
              </Flex>

              {!isArchived && (
                <CdtVersionSummary categoryId={category.id} isArmed={isCdtSummaryArmed} />
              )}
            </Flex>

            <Flex width="1px" height="100%" background={Color.lightGrayHover} />

            <Flex column gap="5px">
              <Text variant="small4" color={Color.spaceGrayHover}>
                Assortment
              </Text>

              <Flex gap="10px">
                <DistributionButton categoryId={category.id} isDisabled={isArchived} />
              </Flex>
            </Flex>

            <Flex width="1px" height="100%" background={Color.lightGrayHover} />

            <Flex column gap="5px">
              <Text variant="small4" color={Color.spaceGrayHover}>
                Planograms
              </Text>

              <Flex gap="10px">
                <Button
                  color="greenSmoke"
                  variant="inverted"
                  size="small"
                  onClick={() => onStoreFixture(category)}
                  isDisabled={isArchived}
                >
                  View Store Fixtures
                </Button>

                <Button
                  color="greenSmoke"
                  variant="inverted"
                  size="small"
                  onClick={() => loadBatchGenerateItems(category.id)}
                  isDisabled={isArchived}
                  isLoading={isLoadingBatchGenerateItems}
                >
                  View Logs
                </Button>

                <Button
                  color="greenSmoke"
                  variant="inverted"
                  size="small"
                  isDisabled={isArchived}
                  dropdown={[
                    {
                      label: "STORE SUMMARY",
                      onClick: () => onImportStoreSummary(category),
                    },
                    {
                      label: "VARIANT RESTRICTION",
                      onClick: () => onImportVariantRestriction(category),
                    },
                    {
                      label: t("variantRestrictionDescribe.categoriesActionLabel"),
                      onClick: () => onDescribeVariantRestriction(category),
                    },
                    {
                      label: "IMAGES",
                      onClick: () => onImportProductImages(),
                    },
                    {
                      label: t("jdaImport.categoriesActionLabel"),
                      isDisabled: !isJdaImportEnabled,
                      tooltip: !isJdaImportEnabled
                        ? t("jdaImport.categoriesDisabledTooltip")
                        : undefined,
                      onClick: () => onImportJdaBaseline(category),
                    },
                    {
                      label: t("pdfSummaryExclusions.categoriesActionLabel"),
                      onClick: () => onImportPdfSummaryExclusions(category),
                    },
                  ]}
                >
                  Import
                </Button>
              </Flex>
            </Flex>
          </Flex>

          {batchGenerateItems && (
            <Flex maxHeight="300px">
              <SubgridTable
                columns={[
                  {
                    id: "createdAt",
                    header: "Created at",
                  },
                  {
                    id: "storeCode",
                    header: "Store code",
                  },
                  {
                    id: "pmUpload",
                    header: "PM status",
                    renderer: ({ value }) => (
                      <TruncatedStatusCell status={uploadStatusToString(value)} title="PM Status" />
                    ),
                  },
                  {
                    id: "ffUpload",
                    header: "FF status",
                    renderer: ({ value }) => (
                      <TruncatedStatusCell status={uploadStatusToString(value)} title="FF Status" />
                    ),
                  },
                ]}
                data={batchGenerateItems}
              />
            </Flex>
          )}
        </Flex>
      </Flex>
    </SubCategoryContainer>
  );
};
